/**
 * TOSS Web Application — Client portal: «установить» and «обновить», in the portal's own voice.
 *
 * The shared `PWAInstallBanner` / `PWAUpdateBanner` are not reused, for two reasons that both
 * matter:
 *
 *   1. **Geometry.** They are `fixed` at `z-50`, pinned to `top-16` and `bottom-20`. The portal's
 *      chrome sits at `--z-sticky` (1020) and is 64 px tall PLUS the safe area — so on a phone with
 *      a notch the install card landed under the header and the update strip under the bottom bar,
 *      hidden by both. Here the two ALERTS — offline and update — are strips in the FLOW, next to
 *      each other, which removes the z-index question and the safe-area arithmetic at once; the
 *      install INVITATION is `fixed` above the chrome and clears it by arithmetic of its own
 *      (see {@link ClientInstallPrompt}), because an invitation may interrupt the view but must
 *      never move the content.
 *   2. **Voice.** They say «Установите приложение» and mean «ТОСС». A customer is installing
 *      «Кабинет» — a different product with a different manifest, and Р21 gave it its own entry
 *      point precisely so the two are not confused.
 *
 * They mount inside `ClientLayout`, which only renders under `ClientProtectedLayout`. That is what
 * keeps them off `/client/login`: no path check is needed, because the anonymous screens do not
 * have this layout at all.
 */

import { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw, Share, WifiOff, X } from 'lucide-react';
import { usePWA } from '@/hooks/usePWA';
import {
  isInstallDismissed,
  rememberInstallDismissal,
  useClientInstallOffer,
} from './useClientInstallOffer';

// ============================================================================
// Update
// ============================================================================

/**
 * «Доступна новая версия». A strip, not a card: it says one thing and offers one action.
 *
 * It could not appear at all until the service worker stopped calling `skipWaiting()` during
 * install — the event it hangs on was never fired. See `src/sw.ts`.
 */
export function ClientUpdateBanner(): JSX.Element | null {
  const { updateAvailable, applyUpdate } = usePWA();
  const [isApplying, setApplying] = useState(false);

  if (!updateAvailable) return null;

  const apply = async (): Promise<void> => {
    setApplying(true);
    try {
      await applyUpdate();
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-2 bg-[var(--color-primary-50)] border-b border-[var(--color-primary-100)]"
      role="status"
    >
      <span className="flex items-center gap-2 min-w-0 text-sm text-[var(--color-primary-700)]">
        <RefreshCw className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        <span className="truncate">Доступна новая версия кабинета</span>
      </span>
      <button
        type="button"
        onClick={() => void apply()}
        disabled={isApplying}
        className="flex-shrink-0 text-sm font-medium text-[var(--color-primary-700)] hover:text-[var(--color-primary-800)] disabled:opacity-60 transition-colors"
      >
        {isApplying ? 'Обновляем…' : 'Обновить'}
      </button>
    </div>
  );
}

// ============================================================================
// Offline
// ============================================================================

/**
 * «Нет подключения к интернету» strip.
 *
 * Lived inside `ClientLayout` until 2026-08-24, when it had to become importable: on the four
 * phone screens that draw their own app bar the layout's copy rendered at document top, UNDER
 * that `fixed` bar, and could not be seen at all. It is now rendered by the bar itself — see
 * {@link ClientPinnedBanners}.
 *
 * The portal has no offline data at all: the service worker no longer caches authenticated API
 * responses (that cache was keyed by URL alone and leaked one tenant's data to the next session
 * on a shared device). So a customer on a flaky phone connection needs to be told the difference
 * between "empty" and "offline" before they read a screen's empty state as the truth.
 */
export function ClientOfflineBanner(): JSX.Element | null {
  const [isOffline, setIsOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false
  );

  useEffect(() => {
    const goOffline = (): void => setIsOffline(true);
    const goOnline = (): void => setIsOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!isOffline) return null;

  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 px-4 py-1.5 flex-shrink-0 bg-[var(--color-warning-subdued)] text-[var(--color-warning-active)] text-sm"
    >
      <WifiOff className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
      <span>Нет подключения к интернету. Данные могут быть неактуальны.</span>
    </div>
  );
}

/**
 * The two strips that must be SEEN, for a bar to render inside itself.
 *
 * Both say something the customer cannot act on from anywhere else — one that the data on screen
 * may be stale, one that a newer build is waiting — and both used to be invisible on «Главная»,
 * «Заявки», «Документы» and «Профиль»: `ClientLayout` puts them in the flow above `<main>`, and on
 * those four routes the flow starts UNDER the screen's own `fixed` app bar. Measured 2026-08-24:
 * `top: 0`, height 35, `document.elementFromPoint` at their centre returning the bar's `<h1>`.
 *
 * Inside the bar they need no z-index and no safe-area arithmetic — the bar already carries both —
 * and the bar's `ResizeObserver` grows its spacer by exactly their height, so the content below
 * shifts down the same way a strip in the flow would shift it.
 *
 * The install CARD deliberately stays out: it is an invitation, not an alert, and a card this tall
 * inside a bar that already holds search, filters and chips would take half the screen.
 */
export function ClientPinnedBanners(): JSX.Element {
  return (
    <>
      <ClientOfflineBanner />
      <ClientUpdateBanner />
    </>
  );
}

/** How long a customer works before the portal offers anything at all (owner decision). */
const OFFER_DELAY_MS = 30_000;

// ============================================================================
// Install: the floating prompt
// ============================================================================

export interface ClientInstallPromptProps {
  /**
   * Where the card sits. The phone gets a full-width card above the bottom bar, the computer a
   * narrow one in the corner — two products, not one layout at two widths (rework rule 4).
   */
  layout: 'phone' | 'desktop';
}

/**
 * «Добавьте кабинет на главный экран» — over the content, never inside it.
 *
 * It used to be a card in the FLOW between the header and `<main>`, and on a computer that root is
 * `h-dvh overflow-hidden`: the card took 86 px of the window and pushed every list down by exactly
 * that much, on every screen, until someone closed it. The owner called it out on 25.08.2026, and
 * the fix is the one thing an invitation may not do — occupy layout. `fixed` at `--z-fixed` (1030,
 * above the bottom bar and the FAB at `--z-sticky`), so nothing below it moves by a pixel.
 *
 * **It waits 30 seconds** (owner decision). A card that greets a customer at load is an
 * interruption before there is anything to interrupt; after half a minute on a screen the person
 * is using the cabinet, and «пусть открывается в одно касание» is an answer to something they now
 * have an opinion about.
 *
 * Closed with the cross → silent for a week, under the portal's own key. Closed for good once the
 * app is actually installed. Either way the offer itself stays reachable — «Установить приложение»
 * in the account menu on a computer, the same row in the profile hub on a phone — which is why
 * this component may be as shy as it is.
 */
export function ClientInstallPrompt({ layout }: ClientInstallPromptProps): JSX.Element | null {
  const { isAvailable, isIOS, install, iosHint } = useClientInstallOffer();
  const [dismissed, setDismissed] = useState(() => isInstallDismissed());
  const [showIOSHint, setShowIOSHint] = useState(false);
  // Two steps, not one: `isDue` is the timer, `isVisible` the frame after mount that the
  // transition needs a starting point in. Setting both at once would paint the card in place.
  const [isDue, setDue] = useState(false);
  const [isVisible, setVisible] = useState(false);

  const canOffer = isAvailable && !dismissed;

  useEffect(() => {
    if (!canOffer) return;

    const timer = window.setTimeout(() => setDue(true), OFFER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [canOffer]);

  useEffect(() => {
    if (!isDue) return;

    const frame = window.requestAnimationFrame(() => setVisible(true));
    return () => window.cancelAnimationFrame(frame);
  }, [isDue]);

  const dismiss = useCallback(() => {
    rememberInstallDismissal(String(Date.now()));
    setDismissed(true);
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIOS) {
      setShowIOSHint(true);
      return;
    }
    const accepted = await install();
    if (accepted) setDismissed(true);
  }, [install, isIOS]);

  if (!canOffer || !isDue) return null;

  // The phone card clears the bottom bar (64 px + safe area) and NOTHING else: it sits ON the
  // «новая заявка» circle instead of above it. Lifting it over the circle (76 px) parked the card
  // in the middle of the screen, over the list, with the circle peeking out below — the owner
  // called it out on 25.08.2026. `--z-fixed` (1030) covers the FAB's `--z-sticky` (1020), and the
  // card is taller than the 56 px circle, so the circle is hidden whole while the offer is up.
  const phoneBottom = 'calc(64px + env(safe-area-inset-bottom, 0px) + 12px)';

  const position =
    layout === 'phone'
      ? 'left-3 right-3'
      : 'right-4 bottom-4 w-[336px] max-w-[calc(100vw-2rem)]';

  return (
    <div
      role="dialog"
      aria-label="Установка кабинета"
      style={layout === 'phone' ? { bottom: phoneBottom } : undefined}
      className={`
        fixed ${position} z-[var(--z-fixed)]
        rounded-[var(--radius-lg)] border border-[var(--color-border-light)]
        bg-[var(--color-bg-card)] shadow-[var(--shadow-overlay)] p-3
        transition-[opacity,transform] duration-200 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
      `}
    >
      <div className="flex items-start gap-3">
        <span
          className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-[var(--radius-md)] bg-[var(--color-primary-50)] text-[var(--color-primary-600)]"
          aria-hidden="true"
        >
          <Download className="w-4 h-4" />
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-[var(--color-text-primary)]">
            Добавьте кабинет на главный экран
          </p>
          <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
            Заявки и документы будут открываться в одно касание, без адресной строки.
          </p>

          {showIOSHint ? (
            <p className="mt-2 flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
              <Share className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
              {iosHint}
            </p>
          ) : (
            <button
              type="button"
              onClick={() => void handleInstall()}
              className="mt-2 text-sm font-medium text-[var(--color-primary-600)] hover:text-[var(--color-primary-700)] transition-colors"
            >
              {isIOS ? 'Как установить' : 'Установить'}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Скрыть предложение установить кабинет"
          className="flex-shrink-0 p-1 -m-1 rounded text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
